/**
 * Owner Audit — operational activity feed.
 *
 * Note: the backend ships `/api/owner/recent-activity` (transactions +
 * redemptions, brand-scoped) but does not currently expose a brand-
 * scoped audit-log endpoint (only the super_admin endpoint exists, and
 * we don't elevate Owner). So this page surfaces the operational
 * activity stream — the things owners actually need to spot anomalies:
 * stamp scans, redemptions, customer behaviour.
 *
 * Filters (client-side):
 *   - Type: all | transactions | redemptions
 *   - Search: customer name / membership ID / branch / reward / invoice
 *   - Limit: pagination cursor
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Receipt, Gift, Search, FileText } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { formatDateTime, formatRupiah } from '@/lib/utils';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, EmptyState, StatusPill,
} from '@/components/ui_primitives/Misc';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui_primitives/Table';
import { Input, Select } from '@/components/ui_primitives/Field';
import { JsonViewer } from '@/components/ui_primitives/Misc';
import TransactionDetailsModal from '@/components/TransactionDetailsModal';

const PAGE_SIZE = 50;

export default function OwnerAuditPage() {
  const token = useAuthStore((s) => s.token);
  const brandId = useAuthStore((s) => s.user?.brand_id);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [typeF, setTypeF] = useState('all');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [activeTx, setActiveTx] = useState(null);

  const activityQ = useQuery({
    queryKey: qk.ownerRecentActivity(brandId, limit),
    queryFn: () => api.ownerRecentActivity(token, limit),
    enabled: !!token && !!brandId,
  });

  const items = activityQ.data || [];
  const filtered = useMemo(() => {
    let arr = items;
    if (typeF === 'transactions') arr = arr.filter((x) => x.type === 'transaction');
    else if (typeF === 'redemptions') arr = arr.filter((x) => x.type === 'redemption');
    if (q.trim()) {
      const needle = q.toLowerCase();
      arr = arr.filter((x) =>
        [x.customer_name, x.membership_id, x.branch_name, x.reward_title, x.invoice_number, x.code]
          .filter(Boolean).some((s) => String(s).toLowerCase().includes(needle))
      );
    }
    return arr;
    // items, typeF, q are the inputs — explicit dep list keeps lint happy
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityQ.data, typeF, q]);

  if (activityQ.isLoading) return <FullSpinner />;
  if (activityQ.error) return <QueryError error={activityQ.error} onRetry={() => activityQ.refetch()} />;

  return (
    <div className="space-y-6" data-testid="owner-audit-page">
      <PageHeader
        kicker="Activity Log"
        title="Brand activity feed"
        subtitle={`Operational events (transactions & redemptions) scoped to your brand. ${items.length} loaded.`}
      />

      <SectionCard padded>
        <div className="grid md:grid-cols-3 gap-3 mb-5">
          <div className="md:col-span-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, membership, branch, invoice, reward…" className="pl-9" testID="audit-search" />
            </div>
          </div>
          <Select value={typeF} onChange={(e) => setTypeF(e.target.value)} testID="audit-type">
            <option value="all">All events</option>
            <option value="transactions">Transactions only</option>
            <option value="redemptions">Redemptions only</option>
          </Select>
        </div>

        {filtered.length === 0 ? (
          <EmptyState icon={<FileText size={20} />} title="Nothing matches" subtitle="Adjust filters or load more events." />
        ) : (
          <Table testID="audit-table">
            <THead>
              <Tr>
                <Th>Type</Th>
                <Th>Customer</Th>
                <Th>Branch</Th>
                <Th>Detail</Th>
                <Th>When</Th>
                <Th>Status</Th>
              </Tr>
            </THead>
            <TBody>
              {filtered.map((ev) => {
                const isOpen = expanded === `${ev.type}-${ev.id}`;
                const isTx = ev.type === 'transaction';
                const hasReceipt = isTx && !!ev.receipt_url;
                return (
                  <>
                    <Tr
                      key={`${ev.type}-${ev.id}`}
                      onClick={() => {
                        if (isTx) {
                          setActiveTx(ev);
                        } else {
                          setExpanded(isOpen ? null : `${ev.type}-${ev.id}`);
                        }
                      }}
                    >
                      <Td>
                        {ev.type === 'redemption' ? (
                          <StatusPill variant="green"><Gift size={10} /> redemption</StatusPill>
                        ) : (
                          <span className="inline-flex items-center gap-1.5">
                            <StatusPill variant="gold"><Receipt size={10} /> transaction</StatusPill>
                            {hasReceipt ? (
                              <span
                                data-testid={`audit-receipt-chip-${ev.id}`}
                                className="inline-flex items-center gap-1 text-[10px] font-semibold text-gold/90 bg-gold/10 border border-gold/30 rounded-full px-2 py-0.5"
                                title="Receipt attached"
                              >
                                <FileText size={9} /> receipt
                              </span>
                            ) : null}
                          </span>
                        )}
                      </Td>
                      <Td>
                        <div className="font-medium">{ev.customer_name}</div>
                        <div className="text-[11px] text-white/40 font-mono">{ev.membership_id}</div>
                      </Td>
                      <Td>{ev.branch_name || '—'}</Td>
                      <Td>
                        {ev.type === 'transaction' ? (
                          <div>
                            <div className="font-mono text-xs text-white/80">{ev.invoice_number}</div>
                            <div className="text-[11px] text-white/40">{formatRupiah(ev.amount_rupiah)} · +{ev.stamps_earned} stamps</div>
                          </div>
                        ) : (
                          <div>
                            <div className="font-medium text-white">{ev.reward_title}</div>
                            <div className="text-[11px] text-white/40 font-mono">code {ev.code} · −{ev.stamps_used} stamps</div>
                          </div>
                        )}
                      </Td>
                      <Td><span className="text-xs text-white/60">{formatDateTime(ev.at)}</span></Td>
                      <Td>
                        {ev.type === 'redemption'
                          ? <StatusPill variant={ev.status === 'used' ? 'green' : ev.status === 'expired' ? 'red' : 'amber'}>{ev.status}</StatusPill>
                          : <StatusPill variant="slate">recorded</StatusPill>}
                      </Td>
                    </Tr>
                    {isOpen && !isTx ? (
                      <Tr>
                        <Td className="bg-black/30" colSpan={6}>
                          <JsonViewer data={ev} />
                        </Td>
                      </Tr>
                    ) : null}
                  </>
                );
              })}
            </TBody>
          </Table>
        )}

        {items.length === limit ? (
          <div className="text-center mt-4">
            <button
              onClick={() => setLimit((l) => l + PAGE_SIZE)}
              data-testid="audit-load-more"
              className="px-4 py-2 text-xs font-semibold rounded-lg bg-white/5 border border-white/10 hover:bg-white/10"
            >
              Load more
            </button>
          </div>
        ) : null}
      </SectionCard>

      {activeTx ? (
        <TransactionDetailsModal tx={activeTx} onClose={() => setActiveTx(null)} />
      ) : null}
    </div>
  );
}
